import { createStore } from '@xstate/store';
import { DEFAULT_TEAMS, updateTeamDefinitions, type TeamDefinition } from '../types';

// ============================================
// Teams Store - Verwaltet die Teams
// ============================================

const TEAMS_LOCAL_STORAGE_KEY = 'roadmap_2026_teams';
const PERSISTENCE_DEBOUNCE_MS = 500;

export interface TeamsContext {
  teams: TeamDefinition[];
}

// Lade Teams aus LocalStorage
function loadTeamsFromLocalStorage(): TeamDefinition[] {
  try {
    const saved = localStorage.getItem(TEAMS_LOCAL_STORAGE_KEY);
    if (!saved) return [...DEFAULT_TEAMS];

    const parsed = JSON.parse(saved) as TeamDefinition[];
    if (!Array.isArray(parsed) || parsed.length === 0) {
      return [...DEFAULT_TEAMS];
    }
    return parsed;
  } catch {
    return [...DEFAULT_TEAMS];
  }
}

// Speichere Teams in LocalStorage
function saveTeamsToLocalStorage(teams: TeamDefinition[]): void {
  localStorage.setItem(TEAMS_LOCAL_STORAGE_KEY, JSON.stringify(teams));
}

const initialTeams = loadTeamsFromLocalStorage();
// Initialisiere TEAM_DEFINITIONS mit den geladenen Teams
updateTeamDefinitions(initialTeams);

const initialContext: TeamsContext = {
  teams: initialTeams,
};

export const teamsStore = createStore({
  context: initialContext,

  on: {
    'team.add': (ctx, event: { team: TeamDefinition }) => {
      // Prüfe ob ID bereits existiert
      if (ctx.teams.some(t => t.id === event.team.id)) {
        console.warn(`[teamsStore] Team with ID "${event.team.id}" already exists`);
        return ctx;
      }
      const newTeams = [...ctx.teams, event.team];
      updateTeamDefinitions(newTeams);
      return {
        ...ctx,
        teams: newTeams,
      };
    },

    'team.update': (ctx, event: { teamId: string; updates: Partial<Omit<TeamDefinition, 'id'>> }) => {
      const teamIndex = ctx.teams.findIndex(t => t.id === event.teamId);
      if (teamIndex === -1) {
        console.warn(`[teamsStore] Team with ID "${event.teamId}" not found`);
        return ctx;
      }

      const updatedTeams = [...ctx.teams];
      updatedTeams[teamIndex] = {
        ...updatedTeams[teamIndex],
        ...event.updates,
      };
      updateTeamDefinitions(updatedTeams);

      return {
        ...ctx,
        teams: updatedTeams,
      };
    },

    'team.delete': (ctx, event: { teamId: string }) => {
      const teamIndex = ctx.teams.findIndex(t => t.id === event.teamId);
      if (teamIndex === -1) {
        console.warn(`[teamsStore] Team with ID "${event.teamId}" not found`);
        return ctx;
      }
      const newTeams = ctx.teams.filter(t => t.id !== event.teamId);
      updateTeamDefinitions(newTeams);
      return {
        ...ctx,
        teams: newTeams,
      };
    },

    'teams.reset': () => {
      updateTeamDefinitions([...DEFAULT_TEAMS]);
      return {
        teams: [...DEFAULT_TEAMS],
      };
    },

    'teams.import': (ctx, event: { teams: TeamDefinition[] }) => {
      updateTeamDefinitions(event.teams);
      return {
        ...ctx,
        teams: event.teams,
      };
    },
  },
});

// ============================================
// Side Effects: Debounced Persistence
// ============================================

let saveTimeout: ReturnType<typeof setTimeout> | null = null;
let lastTeamsHash = '';

function computeTeamsHash(teams: TeamDefinition[]): string {
  return teams.map(t => `${t.id}:${t.name}:${t.color}`).join('|');
}

// Subscribe to changes and persist
teamsStore.subscribe((snapshot) => {
  const currentHash = computeTeamsHash(snapshot.context.teams);

  if (currentHash === lastTeamsHash) return;
  lastTeamsHash = currentHash;

  // Debounced persistence
  if (saveTimeout) clearTimeout(saveTimeout);
  saveTimeout = setTimeout(() => {
    saveTeamsToLocalStorage(snapshot.context.teams);
  }, PERSISTENCE_DEBOUNCE_MS);
});

// ============================================
// Helper-Funktionen
// ============================================

export function getTeamsArray(): TeamDefinition[] {
  return teamsStore.getSnapshot().context.teams;
}

export function getTeamById(teamId: string): TeamDefinition | undefined {
  return teamsStore.getSnapshot().context.teams.find(t => t.id === teamId);
}

// Generiert eine neue eindeutige Team-ID
export function generateTeamId(baseName: string): string {
  const baseId = baseName
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

  const existingTeams = getTeamsArray();
  let id = baseId || 'new-team';
  let counter = 1;

  while (existingTeams.some(t => t.id === id)) {
    id = `${baseId}-${counter}`;
    counter++;
  }

  return id;
}

// Vordefinierte Farben für Teams
export const TEAM_COLORS = [
  '#3b82f6', // blue
  '#10b981', // emerald
  '#8b5cf6', // violet
  '#f97316', // orange
  '#ec4899', // pink
  '#14b8a6', // teal
  '#f59e0b', // amber
  '#ef4444', // red
  '#6366f1', // indigo
  '#84cc16', // lime
  '#a855f7', // purple
  '#06b6d4', // cyan
];

export type TeamsStore = typeof teamsStore;
